import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth, storage } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  deleteDoc,
  getDocs
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import styles from "../styles/ClubFeed.module.css";

// Enhanced upload helper
const uploadToStorage = async (file, folder = "club_posts") => {
  const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export default function ClubFeed() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [user, setUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [coHandlers, setCoHandlers] = useState([]);
  const [userRole, setUserRole] = useState("none"); // "creator", "co-handler", "member", "none"
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [promoteEmail, setPromoteEmail] = useState("");
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("posts"); // "posts", "media", "members", "about"
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      if (authUser) {
        checkUserRole(authUser.uid);
      }
    });

    // Fetch club details
    const fetchClub = async () => {
      try {
        const clubRef = doc(db, "clubs", clubId);
        const clubSnap = await getDoc(clubRef);

        if (clubSnap.exists()) {
          const clubData = { id: clubSnap.id, ...clubSnap.data() };
          setClub(clubData);

          // Fetch members
          fetchMembers();

          // Fetch co-handlers
          if (clubData.coHandlers) {
            const coHandlerDocs = await Promise.all(
              clubData.coHandlers.map(uid => getDoc(doc(db, "users", uid)))
            );
            const coHandlersData = coHandlerDocs
              .filter(doc => doc.exists())
              .map(doc => ({ uid: doc.id, ...doc.data() }));
            setCoHandlers(coHandlersData);
          }

          // Fetch stats
          fetchStats();
        } else {
          navigate("/clubs");
          return;
        }
      } catch (err) {
        console.error("Error fetching club:", err);
      }
    };

    // Listen to club posts
    const postsRef = collection(db, "club_posts");
    const postsQuery = query(
      postsRef,
      where("clubId", "==", clubId),
      orderBy("createdAt", "desc")
    );

    const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setPosts(postsData);
      setLoading(false);
    });

    fetchClub();

    return () => {
      unsubscribeAuth();
      unsubscribePosts();
    };
  }, [clubId, navigate, checkUserRole, fetchMembers, fetchStats]);

  const fetchMembers = useCallback(async () => {
    try {
      const membersRef = collection(db, "club_members");
      const membersQuery = query(
        membersRef,
        where("clubId", "==", clubId),
        where("status", "==", "active")
      );

      const snapshot = await getDocs(membersQuery);
      const membersData = [];

      for (const docSnap of snapshot.docs) {
        const memberData = docSnap.data();
        const userDoc = await getDoc(doc(db, "users", memberData.userId));
        if (userDoc.exists()) {
          membersData.push({
            id: docSnap.id,
            ...memberData,
            userData: { uid: userDoc.id, ...userDoc.data() }
          });
        }
      }

      setMembers(membersData);
    } catch (err) {
      console.error("Error fetching members:", err);
    }
  }, [clubId]);

  const fetchStats = useCallback(async () => {
    try {
      const postsQuery = query(
        collection(db, "club_posts"),
        where("clubId", "==", clubId)
      );
      const postsSnap = await getDocs(postsQuery);

      const membersQuery = query(
        collection(db, "club_members"),
        where("clubId", "==", clubId),
        where("status", "==", "active")
      );
      const membersSnap = await getDocs(membersQuery);

      setStats({
        totalPosts: postsSnap.size,
        totalMembers: membersSnap.size,
        activeThisWeek: Math.floor(membersSnap.size * 0.7) // Example calculation
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [clubId]);

  const checkUserRole = useCallback(async (userId) => {
    try {
      const memberRef = doc(db, "club_members", `${clubId}_${userId}`);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        const memberData = memberSnap.data();
        setUserRole(memberData.role); // "creator", "co-handler", "member"
      } else {
        setUserRole("none");
      }
    } catch (err) {
      console.error("Error checking role:", err);
    }
  }, [clubId]);

  const handleJoinClub = async () => {
    if (!user) {
      alert("Please login first");
      return;
    }

    setJoining(true);
    try {
      // Add user as member
      await setDoc(doc(db, "club_members", `${clubId}_${user.uid}`), {
        clubId: clubId,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || user.email.split('@')[0],
        role: "member",
        joinedAt: serverTimestamp(),
        status: "active",
        notifications: true
      });

      // Update club member count
      const clubRef = doc(db, "clubs", clubId);
      await updateDoc(clubRef, {
        members: arrayUnion(user.uid),
        memberCount: (club.memberCount || 0) + 1,
        updatedAt: serverTimestamp(),
        recentActivity: serverTimestamp()
      });

      // Add club to user's joined clubs
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        joinedClubs: arrayUnion(clubId),
        updatedAt: serverTimestamp()
      });

      setUserRole("member");
      fetchMembers();
    } catch (err) {
      console.error("Error joining club:", err);
      alert("Failed to join club: " + err.message);
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveClub = async () => {
    if (!window.confirm("Are you sure you want to leave this club?")) return;

    try {
      // Remove from members
      await deleteDoc(doc(db, "club_members", `${clubId}_${user.uid}`));

      // Update club member count
      const clubRef = doc(db, "clubs", clubId);
      await updateDoc(clubRef, {
        members: arrayRemove(user.uid),
        memberCount: (club.memberCount || 1) - 1,
        updatedAt: serverTimestamp()
      });

      // Remove club from user's joined clubs
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        joinedClubs: arrayRemove(clubId),
        updatedAt: serverTimestamp()
      });

      // If user is co-handler, remove from co-handlers list
      if (userRole === "co-handler") {
        await updateDoc(clubRef, {
          coHandlers: arrayRemove(user.uid)
        });
      }

      setUserRole("none");
      fetchMembers();
      navigate("/clubs");
    } catch (err) {
      console.error("Error leaving club:", err);
      alert("Failed to leave club: " + err.message);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      alert("Please enter an email address");
      return;
    }

    if (!["creator", "co-handler"].includes(userRole)) {
      alert("Only creators and co-handlers can invite members");
      return;
    }

    try {
      // Check if user exists
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", inviteEmail)
      );
      const userSnap = await getDocs(usersQuery);

      if (userSnap.empty) {
        alert("User with this email not found");
        return;
      }

      const invitedUser = userSnap.docs[0];
      const invitedUserId = invitedUser.id;

      // Check if already a member
      const memberRef = doc(db, "club_members", `${clubId}_${invitedUserId}`);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        alert("User is already a member");
        return;
      }

      // Send invitation
      await setDoc(doc(db, "club_invitations", `${clubId}_${invitedUserId}`), {
        clubId: clubId,
        clubName: club.name,
        userId: invitedUserId,
        userEmail: inviteEmail,
        invitedBy: user.uid,
        invitedByEmail: user.email,
        role: "member",
        status: "pending",
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
      });

      alert(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
    } catch (err) {
      console.error("Error inviting member:", err);
      alert("Failed to send invitation");
    }
  };

  const handlePromoteToCoHandler = async () => {
    if (!promoteEmail.trim()) {
      alert("Please enter an email address");
      return;
    }

    if (userRole !== "creator") {
      alert("Only club creator can promote members to co-handlers");
      return;
    }

    try {
      // Find user by email
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", promoteEmail)
      );
      const userSnap = await getDocs(usersQuery);

      if (userSnap.empty) {
        alert("User not found");
        return;
      }

      const promotedUser = userSnap.docs[0];
      const promotedUserId = promotedUser.id;

      // Check if user is a member
      const memberRef = doc(db, "club_members", `${clubId}_${promotedUserId}`);
      const memberSnap = await getDoc(memberRef);

      if (!memberSnap.exists()) {
        alert("User is not a member of this club");
        return;
      }

      // Promote to co-handler
      await updateDoc(memberRef, {
        role: "co-handler",
        promotedAt: serverTimestamp(),
        promotedBy: user.uid
      });

      // Add to co-handlers array in club
      const clubRef = doc(db, "clubs", clubId);
      await updateDoc(clubRef, {
        coHandlers: arrayUnion(promotedUserId),
        updatedAt: serverTimestamp()
      });

      alert(`${promoteEmail} has been promoted to co-handler`);
      setPromoteEmail("");
      fetchMembers();
    } catch (err) {
      console.error("Error promoting member:", err);
      alert("Failed to promote member");
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    if (!["creator", "co-handler"].includes(userRole)) {
      alert("Only creators and co-handlers can remove members");
      return;
    }

    if (!window.confirm(`Remove ${memberEmail} from the club?`)) return;

    try {
      // Remove from members
      await deleteDoc(doc(db, "club_members", `${clubId}_${memberId}`));

      // Update club member count
      const clubRef = doc(db, "clubs", clubId);
      await updateDoc(clubRef, {
        members: arrayRemove(memberId),
        memberCount: (club.memberCount || 1) - 1,
        updatedAt: serverTimestamp()
      });

      // If removed user is co-handler, remove from co-handlers
      await updateDoc(clubRef, {
        coHandlers: arrayRemove(memberId)
      });

      // Remove club from user's joined clubs
      const userRef = doc(db, "users", memberId);
      await updateDoc(userRef, {
        joinedClubs: arrayRemove(clubId),
        updatedAt: serverTimestamp()
      });

      alert("Member removed successfully");
      fetchMembers();
    } catch (err) {
      console.error("Error removing member:", err);
      alert("Failed to remove member");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith("image/"));

    if (imageFiles.length > 10) {
      alert("Maximum 10 images allowed per post");
      return;
    }

    setSelectedFiles(prev => [...prev, ...imageFiles].slice(0, 10));
  };

  const handlePost = async () => {
    if (!newPost.trim() && selectedFiles.length === 0) return;
    if (!["creator", "co-handler", "member"].includes(userRole)) {
      alert("You must be a member to post in this club");
      return;
    }

    setIsUploading(true);
    try {
      // Upload images
      const uploadedImages = [];
      for (const file of selectedFiles) {
        const url = await uploadToStorage(file, `clubs/${clubId}/posts`);
        uploadedImages.push({
          url: url,
          type: file.type,
          name: file.name
        });
      }

      // Create post
      const postData = {
        clubId: clubId,
        clubName: club.name,
        text: newPost,
        images: uploadedImages,
        author: {
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
          avatar: user.photoURL
        },
        createdAt: serverTimestamp(),
        likes: [],
        comments: [],
        shares: 0,
        views: 0,
        pinned: false
      };

      const postRef = await addDoc(collection(db, "club_posts"), postData);

      // Update club post count
      const clubRef = doc(db, "clubs", clubId);
      await updateDoc(clubRef, {
        posts: (club.posts || 0) + 1,
        updatedAt: serverTimestamp(),
        recentActivity: serverTimestamp(),
        lastPost: {
          id: postRef.id,
          text: newPost.substring(0, 100) + (newPost.length > 100 ? "..." : ""),
          author: user.email,
          timestamp: serverTimestamp()
        }
      });

      // Reset form
      setNewPost("");
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Error posting:", err);
      alert("Failed to create post");
    } finally {
      setIsUploading(false);
    }
  };

  const handleLike = async (postId, currentLikes = []) => {
    if (!user) return;

    try {
      const postRef = doc(db, "club_posts", postId);
      if (currentLikes.includes(user.uid)) {
        await updateDoc(postRef, {
          likes: arrayRemove(user.uid)
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion(user.uid)
        });
      }
    } catch (err) {
      console.error("Error updating like:", err);
    }
  };


  const handleDeletePost = async (postId, authorId) => {
    if (!["creator", "co-handler"].includes(userRole) && authorId !== user.uid) {
      alert("You can only delete your own posts");
      return;
    }

    if (!window.confirm("Delete this post?")) return;

    try {
      await deleteDoc(doc(db, "club_posts", postId));

      // Update club post count
      const clubRef = doc(db, "clubs", clubId);
      await updateDoc(clubRef, {
        posts: (club.posts || 1) - 1,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error("Error deleting post:", err);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) return <div className={styles.container}>Loading club...</div>;
  if (!club) return <div className={styles.container}>Club not found</div>;

  const isAdmin = ["creator", "co-handler"].includes(userRole);
  const canPost = ["creator", "co-handler", "member"].includes(userRole);

  return (
    <div className={styles.container}>
      {/* Club Header */}
      <div className={styles.header}>
        <div className={styles.banner}>
          {club.bannerUrl ? (
            <img src={club.bannerUrl} alt={`${club.name} banner`} />
          ) : (
            <div className={styles.bannerPlaceholder} style={{ background: club.themeColor || "#1a1a1a" }}>
              {club.name.charAt(0)}
            </div>
          )}
        </div>

        <div className={styles.infoSection}>
          <div className={styles.avatar}>
            {club.logoUrl ? (
              <img src={club.logoUrl} alt={`${club.name} logo`} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {club.name.charAt(0)}
              </div>
            )}
          </div>

          <div className={styles.details}>
            <h1>{club.name}</h1>
            <p className={styles.description}>{club.description}</p>

            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.statNumber}>{formatNumber(club.memberCount || 1)}</span>
                <span className={styles.statLabel}>Followers</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statNumber}>{formatNumber(club.posts || 0)}</span>
                <span className={styles.statLabel}>Posts</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statNumber}>{club.category}</span>
                <span className={styles.statLabel}>Category</span>
              </div>
            </div>

            <div className={styles.actions}>
              {userRole === "none" ? (
                <button
                  className={styles.btnPrimary}
                  onClick={handleJoinClub}
                  disabled={joining}
                >
                  {joining ? "Joining..." : "Follow Club"}
                </button>
              ) : (
                <button
                  className={styles.btnSecondary}
                  onClick={handleLeaveClub}
                >
                  Following ✓
                </button>
              )}

              {isAdmin && (
                <button
                  className={styles.btnOutline}
                  onClick={() => setShowSettings(!showSettings)}
                >
                  ⚙️ Manage
                </button>
              )}

              <button
                className={styles.btnOutline}
                onClick={() => setShowMembers(!showMembers)}
              >
                👥 Members
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "posts" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("posts")}
        >
          Posts
        </button>
        <button
          className={`${styles.tab} ${activeTab === "media" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("media")}
        >
          Media
        </button>
        <button
          className={`${styles.tab} ${activeTab === "about" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("about")}
        >
          About
        </button>
        {isAdmin && (
          <button
            className={`${styles.tab} ${activeTab === "analytics" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            Analytics
          </button>
        )}
      </div>

      {/* Admin Settings Panel */}
      {showSettings && isAdmin && (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Club Management</h3>

          <div className={styles.formGroup}>
            <h4>Invite Members</h4>
            <div className={styles.inputRow}>
              <input
                type="email"
                className={styles.input}
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <button className={styles.btnPrimary} onClick={handleInviteMember}>Send Invite</button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <h4>Promote to Co-handler</h4>
            <div className={styles.inputRow}>
              <input
                type="email"
                className={styles.input}
                placeholder="Enter member's email"
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
              />
              <button className={styles.btnPrimary} onClick={handlePromoteToCoHandler}>Promote</button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <h4>Current Co-handlers</h4>
            <div className={styles.membersGrid}>
              {coHandlers.map(cohandler => (
                <div key={cohandler.uid} className={styles.memberCard}>
                  <div className={styles.memberAvatar}>
                    {cohandler.photoURL ? (
                      <img src={cohandler.photoURL} alt={cohandler.displayName} />
                    ) : (
                      <div className={styles.memberAvatarPlaceholder}>
                        {cohandler.displayName?.charAt(0) || cohandler.email.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className={styles.memberName}>{cohandler.displayName || cohandler.email.split('@')[0]}</div>
                    <div className={styles.memberEmail}>{cohandler.email}</div>
                  </div>
                  {userRole === "creator" && cohandler.uid !== user.uid && (
                    <button
                      className={`${styles.btnDanger} ${styles.btnSm}`}
                      style={{ marginTop: '10px' }}
                      onClick={() => handleRemoveMember(cohandler.uid, cohandler.email)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Members Panel */}
      {showMembers && (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Club Members ({members.length})</h3>
          <div className={styles.membersGrid}>
            {members.map(member => (
              <div key={member.userId} className={styles.memberCard}>
                <div className={styles.memberAvatar}>
                  {member.userData?.photoURL ? (
                    <img src={member.userData.photoURL} alt={member.userData.displayName} />
                  ) : (
                    <div className={styles.memberAvatarPlaceholder}>
                      {member.userData?.displayName?.charAt(0) || member.userEmail.charAt(0)}
                    </div>
                  )}
                  {member.role === "creator" && <span className={styles.roleBadge} title="Creator">👑</span>}
                  {member.role === "co-handler" && <span className={styles.roleBadge} title="Co-handler">⭐</span>}
                </div>
                <div className={styles.memberInfo}>
                  <div className={styles.memberName}>{member.userData?.displayName || member.userEmail.split('@')[0]}</div>
                  <div className={styles.memberEmail}>{member.userEmail}</div>
                  <div className={styles.memberRole}>{member.role}</div>
                </div>
                {isAdmin && member.userId !== user?.uid && (
                  <button
                    className={`${styles.btnDanger} ${styles.btnSm}`}
                    onClick={() => handleRemoveMember(member.userId, member.userEmail)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={styles.content}>
        {activeTab === "posts" && (
          <>
            {canPost && (
              <div className={styles.createPost}>
                <textarea
                  className={styles.textarea}
                  placeholder="Share an update with your club..."
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                  <button onClick={() => fileInputRef.current.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>📷</button>
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />
                  <button className={styles.btnPrimary} onClick={handlePost} disabled={isUploading}>
                    {isUploading ? "Posting..." : "Post"}
                  </button>
                </div>
                {selectedFiles.length > 0 && <div style={{ fontSize: '0.8rem', marginTop: '5px' }}>{selectedFiles.length} images selected</div>}
              </div>
            )}

            <div className={styles.postsList}>
              {posts.map(post => (
                <div key={post.id} className={styles.post}>
                  <div className={styles.postHeader}>
                    <div className={styles.postAuthorInfo}>
                      {post.author?.avatar ? (
                        <img src={post.author.avatar} alt="Author" className={styles.postAvatar} />
                      ) : (
                        <div className={styles.postAvatar} style={{ background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {post.author?.name?.[0] || "U"}
                        </div>
                      )}
                      <div>
                        <div className={styles.postAuthorName}>{post.author?.name}</div>
                        <div className={styles.postTime}>{post.createdAt ? new Date(post.createdAt).toLocaleDateString() : 'Just now'}</div>
                      </div>
                    </div>
                    {(isAdmin || user?.uid === post.author?.uid) && (
                      <button onClick={() => handleDeletePost(post.id, post.author?.uid)} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer' }}>🗑️</button>
                    )}
                  </div>
                  <div className={styles.postContent}>{post.text}</div>
                  {post.images && post.images.length > 0 && (
                    <div className={`${styles.postImages} ${post.images.length > 1 ? styles.grid : ''}`}>
                      {post.images.slice(0, 4).map((img, idx) => (
                        <img key={idx} src={img.url} alt="Post" className={styles.postImage} onClick={() => window.open(img.url, '_blank')} />
                      ))}
                    </div>
                  )}
                  <div className={styles.postActions}>
                    <button className={styles.actionBtn} onClick={() => handleLike(post.id, post.likes)}>
                      ❤️ {post.likes?.length || 0}
                    </button>
                    <button className={styles.actionBtn}>💬 {post.comments?.length || 0}</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {activeTab === "about" && (
          <div className={styles.panel}>
            <h3 className={styles.panelTitle}>About {club.name}</h3>
            <p>{club.description}</p>
            <p><strong>Category:</strong> {club.category}</p>
            <p><strong>Date Created:</strong> {club.createdAt?.toDate?.().toLocaleDateString()}</p>
          </div>
        )}
      </div>
    </div>
  );
}